import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, StatusBadge } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEUR, fmtDate } from "@/lib/format";
import { MF_VERSION_STATUS_MAP } from "@/lib/constants";
import { Plus, Percent, Send, Check, X } from "lucide-react";

const EMPTY = { name: "", external_id: "", provider_id: "", distributor_id: "", valid_from: "", valid_to: "",
  channel: "", bonus_type: "fest", bonus_amount: 0, bonus_percent: 0, conditions: [], exclusions: [], combinable: false };

export default function MfPromotions() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const write = can(user?.role, "mobilfunk:write");
  const approve = can(user?.role, "mobilfunk:approve");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["mf-promotions", q], queryFn: async () => (await api.get(`/mobilfunk/promotions?q=${encodeURIComponent(q)}`)).data });

  const flow = async (p, action) => {
    try { await api.post(`/mobilfunk/promotions/${p.id}/${action}`); qc.invalidateQueries({ queryKey: ["mf-promotions"] }); toast.success("Aktualisiert"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const columns = [
    { key: "name", header: "Aktion", render: (p) => (
      <div className="min-w-0"><div className="flex items-center gap-2 font-medium">{p.name}{p.is_demo && <Badge variant="outline" className="text-[10px]">DEMO</Badge>}</div>
        <div className="text-xs text-muted-foreground">{p.provider_name || "—"} · {fmtDate(p.valid_from)}–{p.valid_to ? fmtDate(p.valid_to) : "offen"}</div></div>
    ) },
    { key: "bonus", header: "Bonus", render: (p) => p.bonus_type === "prozent" ? `${p.bonus_percent} %` : fmtEUR(p.bonus_amount) },
    { key: "status", header: "Status", render: (p) => <StatusBadge status={p.status} map={MF_VERSION_STATUS_MAP} /> },
    { key: "actions", header: "", render: (p) => (
      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {write && p.status === "entwurf" && <Button size="sm" variant="outline" onClick={() => flow(p, "submit")} data-testid={`promo-submit-${p.id}`}><Send className="h-3.5 w-3.5" /></Button>}
        {approve && p.status === "zur_freigabe" && <>
          <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => flow(p, "approve")} data-testid={`promo-approve-${p.id}`}><Check className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="outline" className="text-red-600" onClick={() => flow(p, "reject")} data-testid={`promo-reject-${p.id}`}><X className="h-3.5 w-3.5" /></Button>
        </>}
      </div>
    ) },
  ];

  return (
    <div data-testid="mf-promotions">
      <PageHeader title="Aktionen & Bundles" description={`${data?.total ?? 0} Aktionen`}
        actions={write && <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setEdit(EMPTY)} data-testid="new-promotion-button"><Plus className="h-4 w-4" />Neue Aktion</Button>} />
      <div className="mb-4"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="sm:max-w-sm" data-testid="promo-search" /></div>
      <DataTable columns={columns} rows={data?.items} loading={isLoading} testIdPrefix="promo-row" onRowClick={write ? (p) => p.status !== "freigegeben" && setEdit(p) : undefined} />
      <PromotionDialog value={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); qc.invalidateQueries({ queryKey: ["mf-promotions"] }); }} />
    </div>
  );
}

function PromotionDialog({ value, onClose, onDone }) {
  const [f, setF] = useState(EMPTY);
  const isEdit = value && value.id;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const { data: provs } = useQuery({ queryKey: ["mf-prov-min"], queryFn: async () => (await api.get("/mobilfunk/providers")).data, enabled: !!value });
  const { data: tariffs } = useQuery({ queryKey: ["mf-tariff-min"], queryFn: async () => (await api.get("/mobilfunk/tariffs")).data, enabled: !!value });
  if (value && f.__id !== (value.id || "new")) setF({ ...EMPTY, ...value, __id: value.id || "new" });

  const anbieter = (provs?.items || []).filter((p) => p.kind === "anbieter");
  const distis = (provs?.items || []).filter((p) => p.kind === "distributor");

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name erforderlich");
    const body = { ...f };
    delete body.__id; delete body.is_demo; delete body.status; delete body.provider_name;
    body.bonus_amount = Number(f.bonus_amount) || 0; body.bonus_percent = Number(f.bonus_percent) || 0;
    body.eligible_tariff_ids = f.eligible_tariff_ids || [];
    try {
      if (isEdit) await api.put(`/mobilfunk/promotions/${value.id}`, body);
      else await api.post("/mobilfunk/promotions", body);
      toast.success("Gespeichert"); onDone();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="promotion-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Percent className="h-4 w-4" />{isEdit ? "Aktion bearbeiten" : "Neue Aktion"}</DialogTitle>
          <DialogDescription>Aktionen starten als Entwurf und werden anschließend freigegeben.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="promo-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Anbieter</Label>
              <Select value={f.provider_id || "none"} onValueChange={(v) => set("provider_id", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{anbieter.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Distributor</Label>
              <Select value={f.distributor_id || "none"} onValueChange={(v) => set("distributor_id", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{distis.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Gültig ab</Label><Input type="date" value={f.valid_from || ""} onChange={(e) => set("valid_from", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Gültig bis</Label><Input type="date" value={f.valid_to || ""} onChange={(e) => set("valid_to", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Bonusart</Label>
              <Select value={f.bonus_type} onValueChange={(v) => set("bonus_type", v)}>
                <SelectTrigger data-testid="promo-bonustype"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="fest">Fest (€)</SelectItem><SelectItem value="prozent">Prozentual (%)</SelectItem></SelectContent>
              </Select></div>
            {f.bonus_type === "prozent"
              ? <div className="space-y-1.5"><Label>Bonus %</Label><Input type="number" step="0.01" value={f.bonus_percent} onChange={(e) => set("bonus_percent", e.target.value)} data-testid="promo-bonus-percent" /></div>
              : <div className="space-y-1.5"><Label>Bonus €</Label><Input type="number" step="0.01" value={f.bonus_amount} onChange={(e) => set("bonus_amount", e.target.value)} data-testid="promo-bonus-amount" /></div>}
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.combinable} onCheckedChange={(v) => set("combinable", v)} />Mit anderen Aktionen kombinierbar</label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="promo-save">Speichern</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
